import React, { useMemo } from 'react';
import cx from 'classnames';
import { Input, InputProps } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/Button/Button';
import Loading from 'components/Loading/Loading';
import { ValidationStatus } from 'shared/helpers/isFieldValid';
import InputFieldErrorMessage from 'components/InputFieldErrorMessage/InputFieldErrorMessage';
import { usePasswordManagerInputProps } from 'infrastructure/form/PasswordManagerAutofillContext';
import { isCustomIcon, isIconButton, type TextFieldIcon } from './findIconType';

import './TextField.scss';

const MAX_TIME_FIELD_LENGTH = 5;

export interface TextFieldProps extends InputProps {
  /** Input name & ID */
  name?: string;
  id?: string;
  /** Input type, others are supported in HTML but there should be separate components for those */
  type?:
    | 'email'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'textarea'
    | 'url'
    | 'time';
  /** Validation array */
  maxLength?: number;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  value?: string;
  errors?: string;
  isTouched?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  className?: string;
  /** For testing purposes */
  textFieldId?: string;
  isValid?: ValidationStatus | boolean;
  disabled?: boolean;
  /** Keep disabled automatic translations readable using the locked-field style. */
  locked?: boolean;
  loading?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  innerRef?: React.Ref<HTMLInputElement>;
  rows?: number;
  /** Optional button/icon to show after the input */
  append?: TextFieldIcon;
  /** Optional button/icon to show before the input */
  prepend?: TextFieldIcon;
  disableValidationStyles?: boolean;
  info?: string;
  thinScrollbar?: boolean;
  /** Forces error message to use relative positioning */
  forceRelativeErrorPosition?: boolean;
}

/** Generic input element with some restrictions to ensure better usage */
const TextField: React.FC<TextFieldProps> = props => {
  const passwordManagerInputProps = usePasswordManagerInputProps();
  const {
    className,
    id = '',
    name = '',
    type = 'text',
    maxLength,
    onChange,
    placeholder = '',
    value = '',
    errors = '',
    isTouched = false,
    onBlur,
    onWheel,
    textFieldId,
    isValid,
    disabled = false,
    locked = false,
    loading = false,
    required = false,
    autoFocus = false,
    append,
    prepend,
    disableValidationStyles,
    info,
    thinScrollbar,
    forceRelativeErrorPosition,
    ...rest
  } = props;

  const renderIcon = (icon: TextFieldIcon, position: 'append' | 'prepend') => {
    const containerClassName = `form__form-group-icon input-group-${position}`;

    if (isCustomIcon(icon)) {
      return icon(containerClassName);
    }

    const { color, name: iconName, ...iconProps } = icon;

    const iconEl = <FontAwesomeIcon icon={iconName} />;

    const containerColor = color ?? 'secondary';

    if (isIconButton(iconProps)) {
      return (
        <Button
          color={containerColor}
          className={containerClassName}
          icon={iconEl}
          {...iconProps}
        />
      );
    }

    return (
      <div className={`${containerClassName} btn btn-${containerColor}`}>
        {iconEl}
      </div>
    );
  };

  const hasIcon = prepend || append || loading;

  const rootClassName = cx('TextFieldV2', {
    'form__form-group-field': hasIcon,
  });

  const valid = useMemo(() => {
    if (disableValidationStyles || !isTouched || rest.readOnly) {
      return false;
    }
    if (typeof isValid === 'boolean') {
      return isValid;
    }
    if (isValid === ValidationStatus.Valid) {
      return true;
    }
    return false;
  }, [disableValidationStyles, isTouched, isValid, rest.readOnly]);

  const invalid = useMemo(() => {
    if (disableValidationStyles || !isTouched || rest.readOnly) {
      return false;
    }
    if (typeof isValid === 'boolean') {
      return !isValid;
    }
    if (isValid === ValidationStatus.Invalid) {
      return true;
    }
    return false;
  }, [disableValidationStyles, isTouched, isValid, rest.readOnly]);

  const shouldRenderError = isTouched && Boolean(errors);
  const shouldRenderInfo = !errors && Boolean(info);

  const handleWheel: React.WheelEventHandler<HTMLInputElement> = event => {
    if (type === 'number' && document.activeElement === event.currentTarget) {
      event.currentTarget.blur();
    }
    onWheel?.(event);
  };

  return (
    <div className={rootClassName}>
      <div className="TextFieldV2__inputRow">
        {prepend && renderIcon(prepend, 'prepend')}
        <Input
          disabled={disabled}
          id={id || name}
          maxLength={type === 'time' ? MAX_TIME_FIELD_LENGTH : maxLength}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          onWheel={handleWheel}
          pattern={
            type === 'time' ? '(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]' : undefined
          }
          className={cx('TextFieldV2__input', className, {
            'TextFieldV2__input--locked': disabled && locked,
            'input-icon-left': prepend,
            'input-icon-right': append || loading,
            'thin-scrollbar': thinScrollbar,
          })}
          placeholder={placeholder}
          type={type}
          value={value}
          data-testid={`${textFieldId}__textInput`}
          invalid={invalid}
          valid={valid}
          required={required}
          autoFocus={autoFocus}
          {...passwordManagerInputProps}
          {...rest}
        />
        {append && !loading && renderIcon(append, 'append')}
        {loading &&
          renderIcon(
            containerClassName => (
              <div className={`${containerClassName} btn btn-none`}>
                <Loading visible />
              </div>
            ),
            'append'
          )}
      </div>

      {(shouldRenderError || shouldRenderInfo) && (
        <div
          className={cx('TextFieldV2__messages', {
            'position-relative': forceRelativeErrorPosition,
          })}
        >
          {shouldRenderError ? (
            <InputFieldErrorMessage
              positionRelative={forceRelativeErrorPosition}
              dataTestId={`${textFieldId}__textInputFormFeedback`}
            >
              {errors}
            </InputFieldErrorMessage>
          ) : null}

          {shouldRenderInfo ? (
            <InputFieldErrorMessage
              dataTestId={`${textFieldId}__infoMessage`}
              isInfo
            >
              {info}
            </InputFieldErrorMessage>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default TextField;
